import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Grid, TextField, MenuItem, Button, Tabs, Tab, Avatar, Box } from "@mui/material";
import { certificateApi } from "../certificateApi";
import { idCardApi } from "./idCardApi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface Props {
  form: {
    mode: "AUTO" | "MANUAL";
    studentId?: string;
    photoUrl?: string;
    studentName?: string;
    scholarNo?: string;
    className?: string;
    sectionName?: string;
    dob?: string;
    bloodGroup?: string;
    validUpto?: string;
  };
  onChange: Dispatch<
    SetStateAction<{
      mode: "AUTO" | "MANUAL";
      studentId?: string;
      photoUrl?: string;
      studentName?: string;
      scholarNo?: string;
      className?: string;
      sectionName?: string;
      dob?: string;
      bloodGroup?: string;
      validUpto?: string;
    }>
  >;
  onSubmit: () => void;
}

const StudentIdCardForm: React.FC<Props> = ({ form, onChange, onSubmit }) => {
  const [students, setStudents] = useState<any[]>([]);
  useEffect(() => {
    certificateApi.students({})
      .then((res) => {
        setStudents(res);
      })
      .catch((e) => {
        console.log("IDCard: students error", e);
      });
  }, []);

  const f = (name: string, value: any) => onChange((p) => ({ ...p, [name]: value }));
  const disabled = form.mode === "AUTO";

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await idCardApi.upload(file);
      f("photoUrl", res.url);
    } catch (e) {
      console.error("Upload failed", e);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: "AUTO" | "MANUAL") => {
    f("mode", newValue);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={form.mode} onChange={handleTabChange}>
          <Tab label="Fetch From Student" value="AUTO" />
          <Tab label="Manual Entry" value="MANUAL" />
        </Tabs>
      </Box>

      <Grid container spacing={2} alignItems="center">
        {form.mode === "AUTO" && (
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Select Student"
              value={form.studentId || ""}
              onChange={(e) => {
                const id = e.target.value;
                f("studentId", id);
                const s = students.find((x) => x.id === id);
                if (s?.studentPhotoUrl) f("photoUrl", s.studentPhotoUrl);
              }}
            >
              {students.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.admissionNumber} — {s.firstName} {s.lastName} ({s.class?.name || ""} {s.section?.name || ""})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        <Grid item xs={12} md={2} display="flex" justifyContent="center">
          <Avatar src={form.photoUrl} sx={{ width: 80, height: 80, border: "1px solid #ddd" }} />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            fullWidth
            disabled={disabled && !form.photoUrl} // Allow upload only if manual or replace
          >
            Upload Photo
            <input type="file" hidden accept="image/*" onChange={onFile} disabled={disabled} />
          </Button>
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField fullWidth label="Name" value={form.studentName || ""} onChange={(e) => f("studentName", e.target.value)} disabled={disabled} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth label="Scholar No" value={form.scholarNo || ""} onChange={(e) => f("scholarNo", e.target.value)} disabled={disabled} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth label="Class" value={form.className || ""} onChange={(e) => f("className", e.target.value)} disabled={disabled} />
        </Grid>
        <Grid item xs={12} md={1}>
          <TextField fullWidth label="Section" value={form.sectionName || ""} onChange={(e) => f("sectionName", e.target.value)} disabled={disabled} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth type="date" label="DOB" value={form.dob || ""} onChange={(e) => f("dob", e.target.value)} InputLabelProps={{ shrink: true }} disabled={disabled} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth label="Blood Group" value={form.bloodGroup || ""} onChange={(e) => f("bloodGroup", e.target.value)} disabled={disabled} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth type="date" label="Valid Upto" value={form.validUpto || ""} onChange={(e) => f("validUpto", e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} md={2}>
           {/* Spacer or additional field */}
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onSubmit} size="large">
            Save ID Card
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentIdCardForm;
